import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { APP_CONFIG } from "../../config/app-config";
import { buildApiUrl } from "../api-http/form-url-encoded";

export interface CheckinDock {
  idmuelle: number;
  muelle: string;
  idestatus: number;
  usuariosasignados?: number;
}
export interface CheckinUser {
  idcheckinusuario: number;
  user: string;
  correo: string;
  nombre: string;
  idestatus: number;
  muelles: CheckinDock[];
}
export interface CheckinAdminSnapshot {
  selectedLocation: { idlocacion: number; nombre: string };
  usuarios: CheckinUser[];
  muelles: CheckinDock[];
}
export interface CheckinReservation {
  idcheckinoperacionesreservacion: number;
  totalpax: number;
  idioma: string;
  fecha: string;
  fechareservacion: string;
  estatus: string;
  muelle: string;
  agencia: string;
  barco: string;
  programa: string;
  idtitular: number;
  titular: string;
  titularedad: number;
  titularcorreo: string;
  titularcamarote: string;
}
export interface CheckinPassenger {
  idcheckinoperacionesreservacionpax: number;
  orden: number;
  nombre: string;
  edad: number;
  correo: string;
  camarote: string;
  titular: number;
  relacionmenor: string;
  fecha: string;
  estadofirma: string;
  envelopeid?: string;
  pdfdisponible: number;
  pdfnombre?: string;
}
export interface CheckinCertificate {
  envelopeid: string;
  estatus: string;
  bytescertificado?: number;
  fechadescarga?: string;
  disponible: number;
  nombre?: string;
}
export interface CheckinReservationDetail {
  reservacion: CheckinReservation;
  pasajeros: CheckinPassenger[];
  certificados: CheckinCertificate[];
}
export interface CheckinDisclaimer {
  idcheckindisclaimer: number;
  idestatus: number;
  titulo: string;
  contenido: string;
  textoprefirma: string;
  etiquetafirma: string;
  textopostfirma: string;
  fecha: string;
  fechaactualizacion: string;
  programasids: number[];
  programas: string | null;
}
export interface CheckinProgram {
  idprograma: number;
  programa: string;
}
export interface CheckinLogo {
  rutas3: string;
  mimetype: string;
  bytes: number;
  fechaactualizacion: string;
}
export interface CheckinMonitorError {
  idcheckinmonitorerror: number;
  eventuuid: string;
  usuario: string;
  dispositivoclave: string;
  dispositivo: string;
  androidversion: string;
  appversion: string;
  categoria: string;
  operacion: string;
  mensaje: string;
  intentos: number;
  fechaevento: string;
  fecharecepcion: string;
  idcheckinoperacionesreservacion?: number;
  idcheckinoperacionesreservacionpax?: number;
}

@Injectable()
export class CheckinAdminProvider {
  constructor(private http: HttpClient) {}
  load(
    apiKey: string,
    locationId: number | string,
  ): Promise<CheckinAdminSnapshot> {
    return this.request("get", apiKey, locationId, "bootstrap");
  }
  reservations(
    apiKey: string,
    locationId: number | string,
    date: string,
  ): Promise<{ fecha: string; reservaciones: CheckinReservation[] }> {
    return this.request(
      "get",
      apiKey,
      locationId,
      "reservaciones?fecha=" + encodeURIComponent(date),
    );
  }
  monitor(
    apiKey: string,
    locationId: number | string,
    date: string,
  ): Promise<{ fecha: string; errores: CheckinMonitorError[] }> {
    return this.request(
      "get",
      apiKey,
      locationId,
      "monitor?fecha=" + encodeURIComponent(date),
    );
  }
  reservationDetail(
    apiKey: string,
    locationId: number | string,
    reservationId: number,
  ): Promise<CheckinReservationDetail> {
    return this.request(
      "get",
      apiKey,
      locationId,
      "reservaciones-detalle?idreservacion=" + reservationId,
    );
  }
  signedPdf(
    apiKey: string,
    locationId: number | string,
    reservationId: number,
    passengerId: number,
  ): Promise<Blob> {
    if (!apiKey || !/^[1-9][0-9]*$/.test(String(locationId))) {
      return Promise.reject(new Error("Selecciona una locación válida."));
    }
    const path =
      "checkin-admin/reservaciones-pdf?idlocacion=" +
      encodeURIComponent(String(locationId)) +
      "&idreservacion=" +
      reservationId +
      "&idpasajero=" +
      passengerId;
    const url = buildApiUrl(APP_CONFIG.apiBaseUrl, path);
    return (
      this.http.get(url, {
        headers: new HttpHeaders({ Authorization: apiKey }),
        responseType: "blob" as "blob",
      }) as any
    )
      .toPromise()
      .then((blob: Blob) => this.requirePdf(blob))
      .catch((error) =>
        Promise.reject(
          error instanceof Error
            ? error
            : new Error(
                error && error.status === 403
                  ? "No tienes acceso a esta locación."
                  : "No fue posible descargar el PDF firmado.",
              ),
        ),
      );
  }
  completionCertificate(
    apiKey: string,
    locationId: number | string,
    reservationId: number,
    envelopeId: string,
  ): Promise<Blob> {
    if (
      !apiKey ||
      !/^[1-9][0-9]*$/.test(String(locationId)) ||
      !/^[0-9a-f-]{36}$/i.test(envelopeId || "")
    ) {
      return Promise.reject(
        new Error("El certificado solicitado no es válido."),
      );
    }
    const path =
      "checkin-admin/reservaciones-certificado?idlocacion=" +
      encodeURIComponent(String(locationId)) +
      "&idreservacion=" +
      reservationId +
      "&envelopeid=" +
      encodeURIComponent(envelopeId);
    const url = buildApiUrl(APP_CONFIG.apiBaseUrl, path);
    return (
      this.http.get(url, {
        headers: new HttpHeaders({ Authorization: apiKey }),
        responseType: "blob" as "blob",
      }) as any
    )
      .toPromise()
      .then((blob: Blob) =>
        this.requirePdf(
          blob,
          "El certificado de finalización no está disponible.",
        ),
      )
      .catch((error) =>
        Promise.reject(
          error instanceof Error
            ? error
            : new Error(
                error && error.status === 403
                  ? "No tienes acceso a esta locación."
                  : "No fue posible descargar el certificado de finalización.",
              ),
        ),
      );
  }
  disclaimers(
    apiKey: string,
    locationId: number | string,
  ): Promise<{
    disclaimers: CheckinDisclaimer[];
    programas: CheckinProgram[];
    programassinDisclaimer: CheckinProgram[];
    logo: CheckinLogo | null;
  }> {
    return this.request("get", apiKey, locationId, "disclaimers");
  }
  previewDisclaimerPdf(
    apiKey: string,
    locationId: number | string,
    body: any,
  ): Promise<Blob> {
    if (!apiKey || !/^[1-9][0-9]*$/.test(String(locationId))) {
      return Promise.reject(new Error("Selecciona una locación válida."));
    }
    const url = buildApiUrl(
      APP_CONFIG.apiBaseUrl,
      "checkin-admin/disclaimers/pdf-vista-previa?idlocacion=" +
        encodeURIComponent(String(locationId)),
    );
    return (
      this.http.post(url, body, {
        headers: new HttpHeaders({
          Authorization: apiKey,
          "Content-Type": "application/json",
        }),
        responseType: "blob" as "blob",
      }) as any
    )
      .toPromise()
      .catch((error) =>
        Promise.reject(
          new Error(
            error && error.status === 403
              ? "No tienes acceso a esta locación."
              : "No fue posible generar la vista PDF.",
          ),
        ),
      );
  }
  saveDisclaimer(
    apiKey: string,
    locationId: number | string,
    id: number | null,
    body: any,
  ): Promise<any> {
    return this.request(
      id === null ? "post" : "put",
      apiKey,
      locationId,
      id === null ? "disclaimers" : "disclaimers/" + id,
      body,
    );
  }
  uploadDisclaimerLogo(
    apiKey: string,
    locationId: number | string,
    file: File,
  ): Promise<CheckinLogo> {
    return this.logoRequest(apiKey, locationId, file, "logo");
  }
  previewDisclaimerLogo(
    apiKey: string,
    locationId: number | string,
    file: File,
  ): Promise<{ html: string; mimetype: string; bytes: number }> {
    return this.logoRequest(apiKey, locationId, file, "logo/vista-previa");
  }
  private logoRequest(
    apiKey: string,
    locationId: number | string,
    file: File,
    path: string,
  ): Promise<any> {
    if (!apiKey || !/^[1-9][0-9]*$/.test(String(locationId)) || !file) {
      return Promise.reject(
        new Error("Selecciona un archivo SVG o PNG válido."),
      );
    }
    const form = new FormData();
    form.append("logo", file, file.name);
    const url = buildApiUrl(
      APP_CONFIG.apiBaseUrl,
      "checkin-admin/disclaimers/" +
        path +
        "?idlocacion=" +
        encodeURIComponent(String(locationId)),
    );
    return this.http
      .post<any>(url, form, {
        headers: new HttpHeaders({ Authorization: apiKey }),
      })
      .toPromise()
      .then((response) => {
        if (!response || String(response.code) === "1") {
          throw new Error(
            response && response.message
              ? response.message
              : "No fue posible guardar el logotipo.",
          );
        }
        return response.data;
      })
      .catch((error) =>
        Promise.reject(
          error instanceof Error
            ? error
            : new Error(
                error && error.status === 403
                  ? "No tienes acceso a esta locación."
                  : "No fue posible guardar el logotipo.",
              ),
        ),
      );
  }
  /** A raw endpoint may return an API error as a Blob. Never hand that Blob to
   * the browser as a `.pdf`, because it creates an apparently damaged file. */
  private requirePdf(
    blob: Blob,
    unavailableMessage: string = "El PDF firmado no está disponible.",
  ): Promise<Blob> {
    return new Promise((resolve, reject) => {
      if (!blob || blob.size < 5) {
        reject(new Error(unavailableMessage));
        return;
      }
      const reader = new FileReader();
      reader.onerror = () =>
        reject(new Error("No fue posible verificar el PDF firmado."));
      reader.onload = () => {
        const source = String(reader.result || "");
        if (source.substr(0, 5) === "%PDF-") {
          resolve(blob);
          return;
        }
        let message = unavailableMessage;
        try {
          const payload = JSON.parse(source);
          if (payload && payload.message) {
            message = payload.message;
          }
        } catch (_) {
          /* Not a structured API error. */
        }
        reject(new Error(message));
      };
      reader.readAsText(blob.slice(0, 2048));
    });
  }
  saveUser(
    apiKey: string,
    locationId: number | string,
    id: number | null,
    body: any,
  ): Promise<any> {
    return this.request(
      id === null ? "post" : "put",
      apiKey,
      locationId,
      "usuarios" + (id === null ? "" : "/" + id),
      body,
    );
  }
  private request(
    method: "get" | "post" | "put",
    apiKey: string,
    locationId: number | string,
    path: string,
    body?: any,
  ): Promise<any> {
    if (!apiKey || !/^[1-9][0-9]*$/.test(String(locationId))) {
      return Promise.reject(new Error("Selecciona una locación válida."));
    }
    const url = buildApiUrl(
      APP_CONFIG.apiBaseUrl,
      "checkin-admin/" +
        path +
        (path.indexOf("?") === -1 ? "?" : "&") +
        "idlocacion=" +
        encodeURIComponent(String(locationId)),
    );
    const options = {
      headers: new HttpHeaders({
        Authorization: apiKey,
        "Content-Type": "application/json",
      }),
    };
    const request =
      method === "get"
        ? this.http.get<any>(url, options)
        : this.http[method]<any>(url, body, options);
    return request
      .toPromise()
      .then((response) => {
        if (!response || String(response.code) === "1") {
          throw new Error(
            response && response.message
              ? response.message
              : "No fue posible completar la operación.",
          );
        }
        return response.data;
      })
      .catch((error) => {
        if (error instanceof Error) {
          return Promise.reject(error);
        }
        return Promise.reject(
          new Error(
            error && error.status === 403
              ? "No tienes acceso a esta locación."
              : "No fue posible conectar con el administrador de Check-in.",
          ),
        );
      });
  }
}



// WEBPACK FOOTER //
// ./src/providers/checkin-admin/checkin-admin.ts